import {
  Box,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconTool } from '@tabler/icons-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

export const ProviderDashboardMaintenance = (): ReactElement => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const appearance =
    colorScheme === 'dark'
      ? {
          background: '#242424',
          border: '#333333',
          iconBackground: '#443323',
          iconBorder: '#87521d',
          iconColor: '#e68100',
          title: '#ededf0',
          message: '#c3c3c3',
          support: '#a3a3a3',
        }
      : {
          background: '#ffffff',
          border: '#e4e4e7',
          iconBackground: '#fff4e6',
          iconBorder: '#ffd8a8',
          iconColor: '#c96c00',
          title: '#27272a',
          message: '#52525b',
          support: '#71717a',
        };

  return (
    <Center flex={1} pt={32} pb={{ base: 64, sm: '16vh' }} px={8}>
      <Paper
        w="100%"
        maw={560}
        py={{ base: 28, xs: 40 }}
        px={{ base: 20, xs: 40 }}
        bg={appearance.background}
        style={{ borderColor: appearance.border }}
        withBorder
        radius={16}
      >
        <Stack align="center" gap={24} ta="center">
          <Center
            w={58}
            h={58}
            bg={appearance.iconBackground}
            c={appearance.iconColor}
            style={{
              borderRadius: 14,
              border: `1px solid ${appearance.iconBorder}`,
            }}
            aria-hidden="true"
          >
            <IconTool size={28} stroke={1.8} />
          </Center>
          <Box role="status">
            <Title
              order={2}
              fz={22}
              fw={600}
              lh={1.3}
              c={appearance.title}
              style={{ letterSpacing: -0.4 }}
            >
              {translate('pages:providerDashboard.maintenance.title')}
            </Title>
            <Text fz={16} lh={1.5} c={appearance.message} mt={12}>
              {translate('pages:providerDashboard.maintenance.message')}
            </Text>
          </Box>
          <Group gap={10} justify="center">
            <Button
              component={Link}
              to="/claims"
              variant="filled"
              color="#4ba3e8"
              bg="#4ba3e8"
              c="#102536"
              fw={500}
              h={42}
              radius={8}
              px={22}
            >
              {translate('pages:providerDashboard.maintenance.claims')}
            </Button>
            <Button
              variant="outline"
              color="#4ba3e8"
              c={appearance.title}
              fw={500}
              h={42}
              radius={8}
              px={22}
              onClick={() => window.location.reload()}
            >
              {translate('pages:providerDashboard.maintenance.refresh')}
            </Button>
          </Group>
          <Text fz={14} lh={1.5} c={appearance.support}>
            {translate('pages:providerDashboard.maintenance.support')}
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
};
